import { useState } from 'react';
import { Box, TextField, Text } from '@customerio/pluma-components/react';

interface IPAddressFilterProps {
  value: string;
  onChange: (value: string) => void;
}

// Validates both IPv4 and IPv6 addresses
function isValidIPAddress(ip: string): boolean {
  if (ip.trim() === '') {
    return true; // Empty is valid (no filter applied)
  }

  // IPv4 validation
  const ipv4Regex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  // IPv6 validation (basic check for hex groups separated by colons)
  const ipv6Regex =
    /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;

  return ipv4Regex.test(ip) || ipv6Regex.test(ip);
}

export function IPAddressFilter({ value, onChange }: IPAddressFilterProps) {
  const [localValue, setLocalValue] = useState(value ?? '');
  const isValid = isValidIPAddress(localValue);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();
    setLocalValue(newValue);

    // Only update parent if valid or empty
    if (isValidIPAddress(newValue)) {
      onChange(newValue);
    }
  };

  return (
    <Box p="300">
      {/* Note: Using onInput instead of onChange is required for TextField to work correctly
          in DataTable filter popovers. onChange causes the input to become unresponsive. */}
      <Box>
        <TextField
          label="IP Address"
          value={localValue}
          onInput={handleInput}
          placeholder="Enter IP address (e.g., 192.168.1.1)"
          id="audit-log-filter-ip-address"
          style={!isValid ? { borderColor: themeVars.color['border-critical'] } : undefined}
        />
        {!isValid ? (
          <Text size="xs" color="danger" mt="100">
            Please enter a valid IPv4 or IPv6 address
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}
